import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { compose } from 'redux';
import * as Yup from 'yup';

import RegisterHeader from '../../../../components/headers/register-header';
import Header1 from '../../../../components/headers/header1';
import Header2 from '../../../../components/headers/header2';
import { Column, Row } from '../../../../components/layout';
import { AppForm, AppFormField, AppFormSubmitButton } from '../../../../components/forms';
import AppSpinner from '../../../../components/app-spinner';
import NewErrorMessage from '../../../../components/new-error-message';
import { Icons } from '../../../../util/icons';
import colors from '../../../../util/colors';
import * as fonts from '../../../../util/fonts';
import { strings } from '../../../../util/strings';
import { deepStringObjectMerger } from '../../../../util/utility';
import { post } from '../../../../api/api';
import { uriCardValidate } from '../../../../api/endpoints';
import { addToWizard } from '../../../../store/actions';
import './styles.scss';

interface InsuranceCardNumberProps {
  user?: any;
  wizard?: any;
  globalStyles?: any;
  labels?: typeof strings.pt.register;
  labelsMessages?: typeof strings.pt.messages;
  registoCMDLables?: typeof strings.pt.registoCMD;
  addToWizard?(value: any): void;
}

function InsuranceCardNumber({
  user,
  wizard,
  globalStyles,
  labels: labelsFromRedux,
  labelsMessages: labelsMessagesFromRedux,
  registoCMDLables: registoCMDLablesFromRedux,
  addToWizard,
}: InsuranceCardNumberProps) {
  const labels = deepStringObjectMerger(
    strings.pt.register,
    labelsFromRedux,
  );
  const labelsMessages = deepStringObjectMerger(
    strings.pt.messages,
    labelsMessagesFromRedux,
  );
  const registoCMDLables = deepStringObjectMerger(
    strings.pt.registoCMD,
    registoCMDLablesFromRedux,
  );
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const history = useHistory();
  const featureName = 'Register-CardValidate';
  const primaryColor = globalStyles?.primaryColor || colors.blue;

  const handleCMD = () => {
    if (registoCMDLables?.registoCMD_cmdRegisterUrl) {
      window.location.href = registoCMDLables.registoCMD_cmdRegisterUrl;
    }
  };

  const validationSchema = Yup.object().shape({
    cardNumber: Yup.string()
      .required(labelsMessages?.requiredField)
      .matches(/^[a-zA-Z0-9]+$/, labels?.register_cardNumber_validation_error)
      .max(20, labels?.register_cardNumber_validation_error),
  });

  const onSubmit = async (values: { cardNumber: string }) => {
    setSubmitting(true);
    setErrorMessage(null);

    const sessionId =
      wizard?.dataValidation?.currentUser?.sessionId ||
      wizard?.sessionId ||
      '';

    if (addToWizard) {
      addToWizard({
        ...wizard,
        cardNumber: values.cardNumber,
        dataValidation: {
          ...wizard?.dataValidation,
          cardNumber: values.cardNumber,
        },
      });
    }

    try {
      const payload = {
        SessionID: sessionId,
        cardNumber: values.cardNumber,
      };

      const res: any = await post(
        uriCardValidate(),
        JSON.stringify(payload),
        false,
        featureName,
      );

      if (res?.result?.status === 'Success' || res?.status === 200 || !res?.error) {
        history.push('/registo/novo-email');
      } else {
        setErrorMessage(
          res?.result?.message ||
            labels?.register_cardNumber_validation_invalidCard ||
            labelsMessages?.general_errorMessage,
        );
      }
    } catch (error: any) {
      const status = error?.status ?? error?.response?.status;

      if (status === 400) {
        history.push('/registo-bloqueado');
        return;
      }

      const errorMsg =
        error?.response?.data?.result?.message ||
        error?.message ||
        labels?.register_cardNumber_validation_invalidCard ||
        labelsMessages?.general_errorMessage;
      setErrorMessage(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Column className="register-main-column">
      <RegisterHeader />
      <Column className="register-container" style={{ alignItems: 'center' }}>
        <Row
          className="register-header-row"
          style={{ justifyContent: 'center', marginTop: '2.5rem' }}
        >
          <Header1
            style={{
              ...fonts.XL,
              ...fonts.Bold,
              color: colors.black,
              textAlign: 'center',
            }}
          >
            {labels?.register_cardNumber_validation_title}
          </Header1>
        </Row>

        <Column
          style={{
            ...fonts.M,
            alignItems: 'center',
            textAlign: 'center',
            maxWidth: '380px',
            width: '100%',
          }}
        >
          <Header2
            style={{
              ...fonts.M,
              marginBottom: '2rem',
              textAlign: 'center',
              color: colors.darkGrey,
              lineHeight: '1.4',
            }}
          >
            {labels?.register_cardNumber_validation_description}
          </Header2>

          {errorMessage && (
            <Row
              style={{
                width: '100%',
                marginBottom: '1.5rem',
                justifyContent: 'center',
              }}
            >
              <NewErrorMessage
                iconName={Icons.ALERT_PROBLEM}
                colorIcon={colors.alertRed}
                text={errorMessage}
              />
            </Row>
          )}

          <AppForm
            initialValues={{ cardNumber: '' }}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
            style={{ width: '100%' }}
          >
            <Row style={{ marginBottom: '1rem', width: '100%' }}>
              <AppFormField
                name="cardNumber"
                placeholder={labels?.insuranceAndPhone_insuranceNumberPlaceholder}
                showErrorMessage
                className="register-card-input"
              />
            </Row>

            <Row
              style={{
                justifyContent: 'center',
                marginBottom: '3rem',
                width: '100%',
              }}
            >
              <span
                style={{
                  ...fonts.M,
                  color: primaryColor,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  textAlign: 'center',
                }}
                onClick={handleCMD}
              >
                {labels?.register_cardNumber_validation_no_card}
              </span>
            </Row>

            {submitting ? (
              <AppSpinner type="grey" />
            ) : (
              <AppFormSubmitButton
                title={labels?.insuranceAndPhone_confirmButtonText}
                disabled={submitting}
                className="register-submit-button"
                style={{
                  ...fonts.M,
                  ...fonts.Bold,
                  width: '100%',
                  borderRadius: '25px',
                  height: '50px',
                  backgroundColor: primaryColor,
                  color: colors.white,
                  cursor: 'pointer',
                }}
              />
            )}
          </AppForm>
        </Column>
      </Column>
    </Column>
  );
}

const mapStateToProps = ({ user, wizard, stringsDefault, globalStyles }: any) => ({
  user: user?.value,
  wizard: wizard?.value,
  labels: stringsDefault?.value?.pt?.register,
  labelsMessages: stringsDefault?.value?.pt?.messages,
  registoCMDLables: stringsDefault?.value?.pt?.registoCMD,
  globalStyles: globalStyles?.value,
});

const mapDispatchToProps = (dispatch: any) => ({
  addToWizard: (value: any) => dispatch(addToWizard(value)),
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
)(InsuranceCardNumber);
